// src/components/pages/admin-dash/AdminMatchResultStatsByParameter.jsx
import React, { useState } from "react";
import axiosInstance from "../../../utils/AxiosInstance";
import Badge from "../../ui/Badge";
import AdminTable from "./AdminTable";
import AdminMatchResultFilterBar from "./AdminMatchResultFilterBar";
import { parameterLabel } from "../../../constants/parameterLabels";

/**
 * Admin report — win-rate/prize performance grouped by generation
 * parameter (GET /admin/match-result/stats-by-parameter).
 *
 * This is the report roadmap item 3.2 refers to. The endpoint existed
 * before as a self-service, unauthenticated-by-role path
 * (/site/match-result/stats-grouped*) that no frontend ever consumed —
 * moved here, admin-only, alongside 3 new reports (top prizes, hit
 * distribution, extra-ball impact) that share this same filter shape.
 */
const fmtUsd = (n) =>
  typeof n === "number"
    ? new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(n)
    : "—";

export default function AdminMatchResultStatsByParameter() {
  const [typeLottery, setTypeLottery] = useState("");
  const [parameter, setParameter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [didSearch, setDidSearch] = useState(false);

  const fetchStats = async () => {
    setLoading(true);
    setError("");
    try {
      const params = {};
      if (typeLottery) params.typeLottery = typeLottery;
      if (parameter) params.parameter = parameter;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      const res = await axiosInstance.get("/admin/match-result/stats-by-parameter", { params });
      setRows(Array.isArray(res.data) ? res.data : []);
      setDidSearch(true);
    } catch {
      setError("Failed to load stats.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setTypeLottery(""); setParameter(""); setStartDate(""); setEndDate("");
    setRows([]); setDidSearch(false); setError("");
  };

  const columns = [
    { key: "parameter", label: "Parameter", sort: true, value: (r) => parameterLabel(r.parameter),
      render: (r) => <span style={{ color: "var(--txt)" }}>{parameterLabel(r.parameter)}</span> },
    { key: "bestParameter", label: "Best?", sort: true, value: (r) => (r.bestParameter ? 1 : 0),
      render: (r) => r.bestParameter ? <Badge variant="gold">Best</Badge> : <span style={{ color: "var(--txt-3)" }}>—</span> },
    { key: "totalLines", label: "Lines", sort: true, align: "right",
      render: (r) => <span className="tabular-nums">{(r.totalLines ?? 0).toLocaleString()}</span> },
    { key: "totalCollectedUsd", label: "Total collected", sort: true, align: "right",
      render: (r) => <span className="tabular-nums font-medium">{fmtUsd(r.totalCollectedUsd)}</span> },
    { key: "averagePrizePerLine", label: "Avg prize / line", sort: true, align: "right",
      render: (r) => <span className="tabular-nums font-medium" style={{ color: "var(--gold)" }}>{fmtUsd(r.averagePrizePerLine)}</span> },
  ];

  return (
    <div className="space-y-4">
      <header>
        <h1 className="font-semibold mb-1" style={{ fontSize: 22, color: "var(--txt)" }}>
          Performance by parameter
        </h1>
        <p className="text-[12px]" style={{ color: "var(--txt-2)" }}>
          How each generation-parameter combination performs — total lines generated,
          total prize collected, and average prize per line.
        </p>
      </header>

      <AdminMatchResultFilterBar
        typeLottery={typeLottery} setTypeLottery={setTypeLottery}
        parameter={parameter} setParameter={setParameter}
        startDate={startDate} setStartDate={setStartDate}
        endDate={endDate} setEndDate={setEndDate}
        onSearch={fetchStats}
        onReset={handleReset}
        loading={loading}
      />

      {error && (
        <div
          className="rounded-xl px-4 py-3 text-[13px]"
          style={{ background: "var(--red-dim)", border: "1px solid var(--red-border)", color: "var(--red)" }}
        >
          {error}
        </div>
      )}

      <AdminTable
        title={didSearch ? `${rows.length} parameters` : undefined}
        columns={columns}
        rows={rows}
        loading={loading}
        emptyIcon="sliders"
        emptyMessage={didSearch ? "No match results for this filter." : "Search to see results."}
        defaultSort={{ key: "averagePrizePerLine", dir: "desc" }}
      />
    </div>
  );
}
